import React, { useCallback, useMemo, useState } from 'react';
import { VendorCTAType } from '../../redux/vendorCTAsReducer';
import { VendorGroupContainer, ContactFormPlaceholder } from './VendorCTAsGroup.style';
import VendorCtaSelector from '../VendorCtaSelector/VendorCtaSelector';
import Modal from '../Modal';
import { StockCarType } from '../../types/oneGraphTypes';

interface VendorCTAsProps {
  ctas: VendorCTAType[];
  vin: string;
  vehicleId: string;
  market: string;
  mainDealer?: string;
  vehicleType: string;
  vehicleData: StockCarType;
}

const VendorCTAsGroup: React.FC<VendorCTAsProps> = ({
  ctas,
  vin,
  vehicleId,
  market,
  mainDealer,
  vehicleType,
  vehicleData,
}) => {
  const {
    dynamicAttributes,
    engineInfo,
    carline,
    modelInfo,
    mileage,
    model,
    subtitleText,
    carPrices,
  } = vehicleData;
  const manufacturerSpecificItems = vehicleData?.manufacturerSpecificItems;
  // const {
  //   bodyType, // done
  //   modelMake, // done
  //   modelMileage, // done
  //   modelCode, // done
  //   modelID, // done
  //   trimName, // Audi done, not sure about non Audi
  //   modelYear, // done
  //   modelName, // done
  //   modelPrice, // done
  //   modelPriceRetail, // done
  //   stockNumber, // done
  //   fuelType, // done
  // } = vehicleData;

  const bodyType = useMemo(() => {
    const bodyInfo = manufacturerSpecificItems?.cdbItems?.find((item) => item?.id === 'body-type');
    if (bodyInfo?.value?.length) return bodyInfo?.value;
    return '';
  }, [manufacturerSpecificItems?.cdbItems]);

  const isNonAudi = useMemo(() => {
    if (modelInfo?.genericModel?.code?.substring(0, 2) === 'AA') return false;
    return true;
  }, [modelInfo?.genericModel?.code]);

  // Make(code) - like AA (means Audi)
  const modelMake = useMemo(() => {
    const makeCode = modelInfo?.genericModel?.code?.substring(0, 2);
    return makeCode === 'AA' ? 'Audi' : makeCode;
  }, [modelInfo?.genericModel?.code]);

  // Mileage
  const modelMileage = useMemo(() => {
    return mileage?.value?.number;
  }, [mileage?.value?.number]);

  // Model Code
  const modelCode = useMemo(() => {
    if (isNonAudi) {
      return modelInfo?.genericModel?.code;
    }
    return model?.id?.code;
  }, [isNonAudi, model?.id?.code, modelInfo?.genericModel?.code]);

  // Model ID
  const modelID = useMemo(() => {
    if (isNonAudi) {
      return null;
    }
    return carline?.id;
  }, [carline?.id, isNonAudi]);

  // Model Year
  const modelYear = useMemo(() => {
    if (isNonAudi) {
      return modelInfo?.modelyear;
    }
    return model?.salesModelyear;
  }, [isNonAudi, model?.salesModelyear, modelInfo?.modelyear]);

  // Model Price Retail(sale)
  const modelPriceRetail = useMemo(() => {
    const retailPrice = carPrices?.find((item) => item?.type === 'sale');
    if (retailPrice) {
      return retailPrice?.price?.value;
    }
    return null;
  }, [carPrices]);

  // Model Price Regular(list)
  const listPrice = useMemo(() => {
    const regularPrice = carPrices?.find((item) => item?.type === 'list');
    if (regularPrice) {
      return regularPrice?.price?.value;
    }
    return null;
  }, [carPrices]);

  // Stock Number
  const stockNumber = useMemo(() => {
    const stockNumberObject = dynamicAttributes?.find((key) => key?.id === 'VEHICLE_ID');
    if (stockNumberObject) {
      return stockNumberObject?.value;
    }
    return null;
  }, [dynamicAttributes]);

  // Trim Name
  const trimName = useMemo(() => {
    if (subtitleText?.length) return subtitleText;
    return modelInfo?.genericModel?.text;
    return subtitleText;
  }, [modelInfo?.genericModel?.text, subtitleText]);

  // Vehicle Type
  const dataType = useMemo(() => {
    if (isNonAudi) return 'non-audi';
    return vehicleType;
  }, [isNonAudi, vehicleType]);

  const [contactModalOpen, setContactModalOpen] = useState<boolean>(false);

  const onContactModalOpen = useCallback((e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setContactModalOpen(true);
  }, []);

  const onContactModalClose = useCallback(() => {
    setContactModalOpen(false);
  }, []);

  return (
    <>
      <VendorGroupContainer>
        {ctas?.length > 0 &&
          ctas.map((cta: VendorCTAType, index) => {
            const buttonVariant = index === 0 ? 'primary' : 'secondary';

            return (
              <VendorCtaSelector
                ctaDetails={cta}
                variant={buttonVariant}
                vin={vin}
                onContactModalOpen={onContactModalOpen}
                vehicleId={vehicleId}
                vehicleType={vehicleType}
                market={market}
                mainDealer={mainDealer}
              />
            );
          })}
        <div
          className="vendorIntegrations_vehicleInfo"
          data-body={bodyType || 'null'}
          data-make={modelMake || 'null'}
          data-mileage={modelMileage || 'null'}
          data-model-code={modelCode || 'null'}
          data-model-number={modelID || 'null'} // for Audi it returns carline ID for non Audi returns null
          data-model={modelInfo?.genericModel?.text || 'null'}
          data-vin={vin || 'null'}
          data-purchase-price={modelPriceRetail || 'null'} // price key = sale
          data-purchase-list={listPrice || 'null'} // price key = list
          data-msrp={listPrice || '0'}
          data-stock={stockNumber || 'null'}
          data-trim={trimName || 'null'}
          data-type={dataType || 'null'}
          data-fuel-type={engineInfo?.fuel?.text || 'null'} // endpoint data not avail from query used
          data-year={modelYear || 'null'}
        />
      </VendorGroupContainer>
      <Modal isOpen={contactModalOpen} onClose={onContactModalClose} data-cy="contact-dealer-modal">
        <ContactFormPlaceholder>[LOAD CONTACT FORM FOR DEALER]</ContactFormPlaceholder>
      </Modal>
    </>
  );
};

export default VendorCTAsGroup;
